import React, { memo, useState } from 'react';
import { Box, Menu, MenuItem, MenuProps } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { DownloadIcon, FileCheckIcon, PenIcon, TrashIcon, UndoIcon } from '~/assets/icons';
import ModalAlertConfirm from '~/components/common/modal-alert-confirm/ModalAlertConfirm';

type MenuOptionProps = MenuProps & {
  handleEditItem: () => void;
  handleDeleteItem: () => void;
  handleSign: () => void;
  handleRevoke: () => void;
  handleDownload?: () => void;
  enableEdit?: boolean;
  enableDelete?: boolean;
  enableSign?: boolean;
  enableRevoke?: boolean;
};

const MenuOption = (props: MenuOptionProps) => {
  const {
    enableEdit = true,
    enableDelete = true,
    enableSign = false,
    enableRevoke = false,
    handleDownload,
    handleEditItem,
    handleDeleteItem,
    handleSign,
    handleRevoke,
    ...restProps
  } = props;

  const { t } = useTranslation('admin/common');
  const [openAlertRevoke, setOpenAlertRevoke] = useState<boolean>(false);

  return (
    <Menu
      id={`job-option-menu`}
      className='flex w-full'
      keepMounted
      anchorOrigin={{
        vertical: 'bottom',
        horizontal: 'right',
      }}
      transformOrigin={{
        vertical: 'top',
        horizontal: 'right',
      }}
      {...restProps}
    >
      {handleDownload && (
        <MenuItem onClick={handleDownload}>
          <Box className='flex flex-row items-center p-[6px] w-[169px]'>
            <DownloadIcon className='w-[14px] h-[14px]' />
            <Box className='ml-[6px] text-[14px]/[16.8px] font-normal text-[#5A6385]'>{t('download')}</Box>
          </Box>
        </MenuItem>
      )}

      {enableSign && (
        <MenuItem onClick={handleSign}>
          <Box className='flex flex-row items-center p-[6px] w-[169px]'>
            <FileCheckIcon className='w-[14px] h-[14px]' />
            <Box className='ml-[6px] text-[14px]/[16.8px] font-normal text-[#5A6385]'>{t('sign')}</Box>
          </Box>
        </MenuItem>
      )}

      {enableRevoke && (
        <MenuItem onClick={() => setOpenAlertRevoke(true)}>
          <Box className='flex flex-row items-center p-[6px] w-[169px]'>
            <UndoIcon className='w-[14px] h-[14px]' />
            <Box className='ml-[6px] text-[14px]/[16.8px] font-normal text-[#5A6385]'>{t('revoke')}</Box>
          </Box>
        </MenuItem>
      )}

      {enableEdit && (
        <MenuItem onClick={handleEditItem}>
          <Box className='flex flex-row items-center p-[6px] w-[169px]'>
            <PenIcon className='w-[14px] h-[14px]' />
            <Box className='ml-[6px] text-[14px]/[16.8px] font-normal text-[#5A6385]'>{t('edit')}</Box>
          </Box>
        </MenuItem>
      )}

      {enableDelete && (
        <MenuItem onClick={handleDeleteItem} className='mt-[10px] bg-[#FFF0F0]' id='danger'>
          <Box className='flex flex-row items-center p-[6px] w-[169px] '>
            <TrashIcon className='w-[16px] h-[16px]' stroke='#FC0000' fill='#FC0000' />
            <Box className='ml-[6px] text-[14px]/[16.8px] font-normal text-[#FC0000]'>{t('delete')}</Box>
          </Box>
        </MenuItem>
      )}

      <ModalAlertConfirm
        open={openAlertRevoke}
        description={t('estimate.esign.alertRevoke', { ns: 'admin/job' })}
        confirm={t('confirmRevoke', { ns: 'admin/common' })}
        onCancel={() => setOpenAlertRevoke(false)}
        onConfirm={() => {
          handleRevoke();
          setOpenAlertRevoke(false);
        }}
      />
    </Menu>
  );
};

export default memo(MenuOption);
